import classNames from 'classnames';
import React from 'react';
import { globalResizeObserver } from './measure';
import styles from './scrollable.css';
import { COLOR_TOKENS } from './tokens';

export type ScrollableOverflowStyle = 'fade' | 'line' | 'none';

export function Scrollable({
  children,
  direction = 'vertical',
  backgroundColor = 'colorWhite',
  overlapScrollbarOverContent = false,
  overflowStyle = 'fade',
  hideScrollBar = false,
}: {
  children: React.ReactNode;
  direction?: 'vertical' | 'horizontal';
  overlapScrollbarOverContent?: boolean;
  backgroundColor?: keyof typeof COLOR_TOKENS;
  overflowStyle?: ScrollableOverflowStyle;
  hideScrollBar?: boolean;
}) {
  const innerRef = React.useRef<HTMLDivElement>(null);

  const [atStart, setAtStart] = React.useState(true);
  const [atEnd, setAtEnd] = React.useState(false);
  const [scrollbarHeight, setScrollbarHeight] = React.useState(0);

  const updatePosition = React.useCallback(() => {
    const el = innerRef.current;
    if (!el) return;
    const epsilon = overflowStyle === 'line' ? 1 : 3;
    setScrollbarHeight(el.offsetHeight - el.clientHeight);
    if (direction === 'vertical') {
      setAtStart(el.scrollTop < epsilon);
      setAtEnd(el.scrollHeight - el.scrollTop - el.clientHeight < epsilon);
    } else {
      setAtStart(el.scrollLeft < epsilon);
      setAtEnd(el.scrollWidth - el.scrollLeft - el.clientWidth < epsilon);
    }
  }, [direction, overflowStyle]);

  React.useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    updatePosition();
    return globalResizeObserver.observe(el, updatePosition);
  }, [updatePosition]);

  return (
    <div
      className={classNames(
        styles.scrollable,
        direction === 'vertical' ? styles.isVertical : styles.isHorizontal,
        !overlapScrollbarOverContent && styles.moveScrollbar,
        hideScrollBar && styles.hideScrollBar,
      )}
      style={{
        ['--scrollbarHeight' as string]: scrollbarHeight + 'px',
        ['--backgroundColor' as string]: COLOR_TOKENS[backgroundColor],
      }}
    >
      <div
        ref={innerRef}
        className={styles.scrollableInner}
        onScroll={updatePosition}
      >
        {children}
      </div>
      {overflowStyle === 'line' ? (
        <div
          className={classNames(
            styles.scrollLine,
            styles.scrollLineStart,
            atStart && styles.isHidden,
          )}
        />
      ) : overflowStyle === 'fade' ? (
        <>
          {/* Shadows go on top of content, so go after in the DOM */}
          <div
            className={classNames(
              styles.scrollFade,
              styles.scrollFadeStart,
              atStart && styles.isHidden,
            )}
          />
          <div
            className={classNames(
              styles.scrollFade,
              styles.scrollFadeEnd,
              atEnd && styles.isHidden,
            )}
          />
        </>
      ) : null}
    </div>
  );
}
